// One place for the app's mutable state and DOM handles, instead of
// module-level `let`s in main.ts. `Session` holds everything that used to be
// a top-level mutable binding; `Dom` holds every `getElementById` lookup,
// done once at boot.
import type { World } from '../sim/index.ts';
import { LEVELS } from '../sim/index.ts';
import { loadProgress } from './progress.ts';
import type { Progress } from './progress.ts';
import type { AppState } from './game.ts';
import { Onboarding } from './onboarding.ts';
import { SoundEngine } from './sound.ts';

export interface Dom {
  canvas: HTMLCanvasElement;
  hud: HTMLDivElement;
  cityEl: HTMLSpanElement;
  scoreEl: HTMLElement;
  legendEl: HTMLDivElement;
  intro: HTMLDivElement;
  over: HTMLDivElement;
  clearVeil: HTMLDivElement;
  clearTitle: HTMLHeadingElement;
  clearText: HTMLParagraphElement;
  overScore: HTMLDivElement;
  flyOnEl: HTMLSpanElement;
  citiesEl: HTMLDivElement;
  chooseCity: HTMLSpanElement;
  installHint: HTMLDivElement;
  hint1El: HTMLDivElement;
  hint2El: HTMLDivElement;
  soundToggle: HTMLDivElement;
  pauseToggle: HTMLDivElement;
  pauseVeil: HTMLDivElement;
  toMenu: HTMLSpanElement;
}

export function queryDom(): Dom {
  return {
    canvas: document.getElementById('game') as HTMLCanvasElement,
    hud: document.getElementById('hud') as HTMLDivElement,
    cityEl: document.getElementById('city') as HTMLSpanElement,
    scoreEl: document.getElementById('score') as HTMLElement,
    legendEl: document.getElementById('legend') as HTMLDivElement,
    intro: document.getElementById('intro') as HTMLDivElement,
    over: document.getElementById('over') as HTMLDivElement,
    clearVeil: document.getElementById('clearVeil') as HTMLDivElement,
    clearTitle: document.getElementById('clearTitle') as HTMLHeadingElement,
    clearText: document.getElementById('clearText') as HTMLParagraphElement,
    overScore: document.getElementById('overScore') as HTMLDivElement,
    flyOnEl: document.getElementById('flyOn') as HTMLSpanElement,
    citiesEl: document.getElementById('cities') as HTMLDivElement,
    chooseCity: document.getElementById('chooseCity') as HTMLSpanElement,
    installHint: document.getElementById('installHint') as HTMLDivElement,
    hint1El: document.getElementById('hint1') as HTMLDivElement,
    hint2El: document.getElementById('hint2') as HTMLDivElement,
    soundToggle: document.getElementById('soundToggle') as HTMLDivElement,
    pauseToggle: document.getElementById('pauseToggle') as HTMLDivElement,
    pauseVeil: document.getElementById('pauseVeil') as HTMLDivElement,
    toMenu: document.getElementById('toMenu') as HTMLSpanElement,
  };
}

/** Everything main.ts used to keep as module-level `let`s, gathered into one
 *  object so the loop/veil/platform helpers can take it as a parameter
 *  instead of closing over globals. */
export interface Session {
  /** `?dev=1` — unlocks the whole campaign and enables the dev city-hop
   *  shortcut. Set once at boot, read in many places, so it lives on the
   *  session rather than threaded through every function that needs it. */
  readonly dev: boolean;
  /** `?record=1` — every level starts with the sim's command log on, so
   *  `window.__qs.replay()` can hand a phone session to the lab's --replay. */
  readonly record: boolean;
  progress: Progress;
  world: World | null;
  appState: AppState;
  dragPlaneId: number | null;
  /** current `viewScale` (logical-to-CSS-px ratio) */
  vs: number;
  /** fixed-step accumulator, seconds */
  acc: number;
  /** `performance.now()` of the previous rendered frame */
  last: number;
  lastWorldState: World['state'];
  wakeLock: { release(): Promise<void> } | null;
  onboarding: Onboarding | null;
  hint1Shown: boolean;
  hint2Shown: boolean;
  /** the score the HUD counter currently shows — only re-rendered when it changes */
  hudPoints: number;
  readonly sound: SoundEngine;
}

export function createSession(dev: boolean, record = false): Session {
  return {
    dev,
    record,
    progress: loadProgress(LEVELS),
    world: null,
    appState: 'intro',
    dragPlaneId: null,
    vs: 1,
    acc: 0,
    last: 0,
    lastWorldState: 'playing',
    wakeLock: null,
    onboarding: null,
    hint1Shown: false,
    hint2Shown: false,
    hudPoints: -1,
    sound: new SoundEngine(),
  };
}
